import {AfterViewInit, Component, ElementRef, Inject, NgZone, OnDestroy, OnInit, PLATFORM_ID, QueryList, Renderer2, ViewChild, ViewChildren} from '@angular/core';

import {CurrentUserViewModel} from '../../models/current-user.view-model';
import {AppComponent} from '../../../app.component';
import {PrimeNGConfig} from 'primeng/api';
import {MenuService} from '../../../shared/navbar/app.menu.service';
import {ActivatedRoute} from '@angular/router';
import {QueryService} from '../../services/query.service';
import {asapScheduler, combineLatest, merge, Subject, Subscription} from 'rxjs';
import {distinctUntilChanged, map, observeOn, switchMap, take} from 'rxjs/operators';
import {TokenDataProviderService} from '../../services/token-data-provider.service';
import {
  disableBodyScroll,
  enableBodyScroll,
  clearAllBodyScrollLocks
} from 'body-scroll-lock';
import {isPlatformBrowser} from "@angular/common";
import {MobileExperienceConfigService} from '../../mobile/mobile-experience-config.service';
import {MobileExperienceShellService} from '../../mobile/mobile-experience-shell.service';
import {MobileBackCoordinatorService} from '../../mobile/mobile-back-coordinator.service';
import {MobileSearchOverlayService} from '../../mobile/mobile-search-overlay.service';

export interface ItemClickInterface {
  click: boolean;
  previousItem: string;
  nextItem: string;
}

@Component({
  selector: 'app-site',
  templateUrl: './site-layout.component.html',
  styleUrls: ['./site-layout.component.scss']
})
export class SiteLayoutComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('mobileMenuDrawer', {read: ElementRef})
  private mobileMenuDrawerRef?: ElementRef<HTMLElement>;
  @ViewChild('layoutWrapper', {read: ElementRef})
  private layoutWrapperRef?: ElementRef<HTMLElement>;
  @ViewChild('breadcrumb', {read: ElementRef})
  private breadcrumbRef?: ElementRef<HTMLElement>;
  private breadcrumbResizeObserver?: ResizeObserver;
  @ViewChildren('navigationBar', {read: ElementRef})
  private navigationBarRefs!: QueryList<ElementRef<HTMLElement>>;
  private sidebarResizeObserver?: ResizeObserver;
  private sidebarFrameId: number | null = null;
  private removeSidebarListeners?: () => void;
  private destroyed = false;

  get user(): CurrentUserViewModel {
    return this._user;
  }

  set user(value: CurrentUserViewModel) {
    this._user = value;
  }

  topbarMenuActive: boolean;
  topbarVisible: boolean = true;
  footerVisible: boolean = true;
  chatFullscreenMobile: boolean = false;

  menuActive: boolean;

  staticMenuDesktopInactive: boolean;

  mobileMenuActive: boolean;
  mobileTopNavigationActive = false;
  mobileTopNavigationCollapsed = false;
  mobileSearchOverlayOpen = false;

  menuClick: boolean;

  mobileTopbarActive: boolean;

  topbarRightClick: boolean;

  topbarItemClick: boolean;
  mobileTopbarItemClick: boolean;

  activeTopbarItem: string;

  documentClickListener: () => void;

  configClick: boolean;

  rightMenuActive: boolean;

  menuHoverActive = false;

  searchClick = false;
  searchMenuClick = false;
  search = false;

  currentInlineMenuKey: string;

  inlineMenuActive: any[] = [];

  inlineMenuClick: boolean;

  locked = false;
  private activeScrollLockTarget: HTMLElement | null = null;
  private removeDrawerBackHandler: (() => void) | null = null;

  private _user: CurrentUserViewModel;

  public onClickTopBarItem$: Subject<ItemClickInterface> = new Subject<ItemClickInterface>();
  private subscriptions: Subscription[] = [];
  isLoggedIn = false;
  constructor(public renderer: Renderer2, private menuService: MenuService, private primengConfig: PrimeNGConfig,
              public app: AppComponent,
              private route: ActivatedRoute,
              public queryService: QueryService,
              private readonly mobileExperienceConfig: MobileExperienceConfigService,
              private readonly mobileExperienceShell: MobileExperienceShellService,
              private readonly mobileBack: MobileBackCoordinatorService,
              private readonly mobileSearchOverlay: MobileSearchOverlayService,
              @Inject(PLATFORM_ID) private platformId: object,
              private readonly zone: NgZone,
              private readonly tokens: TokenDataProviderService,
  ) {
    this.isLoggedIn = this.route.snapshot.data['isAuthenticated'];
  }

  ngOnInit() {
    // Remote flags only matter once the authenticated application shell exists.
    // Fetching them on /login could turn a recoverable config/API outage into a
    // visible login error before the user has taken any action.
    this.mobileExperienceConfig.initialize();
    this.subscriptions.push(combineLatest([
      this.mobileExperienceShell.active$,
      this.mobileExperienceConfig.config$,
    ]).pipe(
      map(([mobileShellActive, config]) => mobileShellActive && config.mobileTopNavigationEnabled),
      distinctUntilChanged(),
      // Both sources are BehaviourSubjects. A synchronous initial emission
      // during this component's first change-detection pass would otherwise
      // change the topbar *ngIf after Angular has already checked it.
      observeOn(asapScheduler),
    ).subscribe((active) => {
      this.mobileTopNavigationActive = active;
      if (!active) {
        this.mobileTopNavigationCollapsed = false;
        this.mobileSearchOverlay.close();
        this.closeMobileDrawer();
      }
    }));
    this.subscriptions.push(this.mobileSearchOverlay.open$.subscribe((open) => {
      this.mobileSearchOverlayOpen = this.mobileTopNavigationActive && open;
      this.updateBodyScrollLock();
    }));
    this.menuActive = this.isStatic() && !this.isMobile();
    // Preferences are restored after hydration, so the initial menu mode may
    // arrive after this shell's OnInit. Other appearance changes keep its state.
    const appearance = this.app.appearance?.settings$;
    if (appearance) {
      this.subscriptions.push(appearance.pipe(
        map(settings => settings.menuMode),
        distinctUntilChanged(),
        observeOn(asapScheduler),
      ).subscribe(mode => {
        this.closeMobileDrawer();
        this.menuActive = mode === 'static' && this.isDesktop();
        this.staticMenuDesktopInactive = false;
        this.menuService.reset();
      }));
    }
    this.subscriptions.push(merge(
      this.route.data.pipe(map(data => data['currentUser']), take(1)),
      this.tokens.browserReady$.pipe(take(1), switchMap(() => this.tokens.user$)),
    ).subscribe((currentUser: CurrentUserViewModel) => {
      this._user = currentUser;
      this.isLoggedIn = !!currentUser?.['@id'];
    }));
    const sub = this.queryService.fetchResourcesFromQuerySubject().subscribe(() => {
    });
    this.subscriptions.push(sub);
  }

  public onMobileTopNavigationCollapsedChange(collapsed: boolean): void {
    this.mobileTopNavigationCollapsed = this.mobileTopNavigationActive && collapsed;
  }

  ngAfterViewInit() {
    this.observeBreadcrumbHeight();
    this.observeSidebarOffset();
    // hides the horizontal submenus or top menu if outside is clicked
    this.documentClickListener = this.renderer.listen('body', 'click', () => {
      if (!this.topbarItemClick) {
        this.onClickTopBarItem$.next({
          click: this.topbarItemClick,
          previousItem: this.activeTopbarItem,
          nextItem: null
        });
        this.activeTopbarItem = null;
        this.topbarMenuActive = false;
      }

      if (!this.mobileTopbarItemClick) {
        this.mobileTopbarActive = false;
      }

      if (!this.menuClick && (this.isHorizontal() || this.isSlim())) {
        this.menuService.reset();
      }

      // if (this.menuService.configMenuActive && !this.configClick) {
      //   this.menuService.configMenuActive = false;
      // }

      if (!this.menuClick) {
        if (this.mobileMenuActive) {
          this.closeMobileDrawer();
        }

        if (this.isOverlay()) {
          this.menuActive = false;
        }

        this.menuHoverActive = false;
      }

      if (!(this.searchClick || this.searchMenuClick)) {
        this.search = false;
      }

      if (this.inlineMenuActive[this.currentInlineMenuKey] && !this.inlineMenuClick) {
        this.inlineMenuActive[this.currentInlineMenuKey] = false;
      }
      this.inlineMenuClick = false;
      this.searchClick = false;
      this.searchMenuClick = false;
      this.configClick = false;
      this.topbarItemClick = false;
      this.topbarRightClick = false;
      this.menuClick = false;
      this.updateBodyScrollLock();
    });
  }

  onMenuButtonClick(event) {
    this.menuActive = !this.menuActive;
    this.topbarMenuActive = false;
    this.topbarRightClick = true;
    this.menuClick = true;

    if (this.isDesktop()) {
      this.staticMenuDesktopInactive = !this.staticMenuDesktopInactive;
    } else {
      this.mobileMenuActive = !this.mobileMenuActive;
      if (this.mobileMenuActive) {
        this.mobileTopbarActive = false;
        this.registerDrawerBackHandler();
      } else {
        this.removeDrawerBackHandler?.();
        this.removeDrawerBackHandler = null;
      }
    }

    this.updateBodyScrollLock();
    event.preventDefault();
  }

  onTopbarMobileButtonClick(event) {
    this.mobileTopbarActive = !this.mobileTopbarActive;
    this.mobileTopbarItemClick = true;
    this.updateBodyScrollLock();
    event.preventDefault();
  }

  public openMobileDrawer(): void {
    if (this.isDesktop() || this.mobileMenuActive) {
      return;
    }
    this.menuActive = true;
    this.mobileMenuActive = true;
    this.mobileTopbarActive = false;
    // The shared body listener closes the legacy drawer. This is the opening
    // logo tap, not a click outside the drawer.
    this.menuClick = true;
    this.registerDrawerBackHandler();
    this.updateBodyScrollLock();
  }

  public closeMobileDrawer(): void {
    this.menuActive = false;
    this.mobileMenuActive = false;
    this.removeDrawerBackHandler?.();
    this.removeDrawerBackHandler = null;
    this.updateBodyScrollLock();
  }

  onRightMenuButtonClick(event) {
    this.rightMenuActive = !this.rightMenuActive;
    this.updateBodyScrollLock();
    event.preventDefault();
  }

  onMenuClick($event) {
    this.menuClick = true;

    if (this.inlineMenuActive[this.currentInlineMenuKey] && !this.inlineMenuClick) {
      this.inlineMenuActive[this.currentInlineMenuKey] = false;
    }
  }

  onSearchKeydown(event) {
    switch (event.keyCode) {
      case 13:
        break;
      case 27:
        this.search = false;
    }
  }

  emitQuery(query) {
    this.queryService.resultSubject$.next(query);
  }

  onInlineMenuClick(event, key) {
    if (key !== this.currentInlineMenuKey) {
      this.inlineMenuActive[this.currentInlineMenuKey] = false;
    }

    this.inlineMenuActive[key] = !this.inlineMenuActive[key];
    this.currentInlineMenuKey = key;
    this.inlineMenuClick = true;
  }

  onTopbarItemClick(event, item) {
    this.topbarItemClick = true;

    if (this.activeTopbarItem === item) {
      this.activeTopbarItem = null;
    } else {
      this.activeTopbarItem = item;
    }

    if (item === 'search') {
      this.queryService.loading = true;
      this.search = !this.search;
      this.searchClick = !this.searchClick;
      this.searchMenuClick = true;
    }
    this.updateBodyScrollLock();
    this.onClickTopBarItem$.next({click: this.topbarItemClick, previousItem: item, nextItem: this.activeTopbarItem});
    event.preventDefault();
  }

  onTopbarSubItemClick(event) {
    event.preventDefault();
  }

  onRTLChange(event) {
    this.app.isRTL = event.checked;
  }

  onRippleChange(event) {
    this.app.ripple = event.checked;
    this.primengConfig.ripple = event.checked;
  }

  onConfigClick(event) {
    this.configClick = true;
  }

  isDesktop() {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }
    return window.innerWidth > 991;
  }

  isMobile() {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }
    return window.innerWidth <= 991;
  }

  isOverlay() {
    return this.app.menuMode === 'overlay';
  }

  isStatic() {
    return this.app.menuMode === 'static';
  }

  isHorizontal() {
    return this.app.menuMode === 'horizontal';
  }

  isSlim() {
    return this.app.menuMode === 'slim';
  }

  onMainLayoutClick(): void {
    this.mobileTopbarItemClick = false;
  }

  disableBodyScroll() {
    const target = this.resolveScrollLockTarget();
    if (!target) {
      return;
    }

    this.activeScrollLockTarget = target;
    disableBodyScroll(target);
  }

  enableBodyScroll(target: HTMLElement | null = this.activeScrollLockTarget): void {
    if (!target) {
      return;
    }

    enableBodyScroll(target);
    if (this.activeScrollLockTarget === target) {
      this.activeScrollLockTarget = null;
    }
  }

  private updateBodyScrollLock(): void {
    if (!isPlatformBrowser(this.platformId) || !this.isMobile()) {
      return;
    }

    clearAllBodyScrollLocks();
    this.activeScrollLockTarget = null;

    if (this.activeTopbarItem === 'notifications' || this.rightMenuActive || this.mobileSearchOverlayOpen) {
      this.disableBodyScroll();
      return;
    }

    if (this.mobileMenuActive) {
      this.disableBodyScroll();
      return;
    }
  }

  resetActiveMenus(): void {
    this.menuActive = false;
    this.closeMobileDrawer();
    this.topbarMenuActive = false;
    this.mobileTopbarActive = false;
    this.updateBodyScrollLock();
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.breadcrumbResizeObserver?.disconnect();
    this.sidebarResizeObserver?.disconnect();
    this.removeSidebarListeners?.();
    if (this.sidebarFrameId !== null) {
      window.cancelAnimationFrame(this.sidebarFrameId);
      this.sidebarFrameId = null;
    }
    if (this.documentClickListener) {
      this.documentClickListener();
    }

    this.subscriptions.forEach(s => s.unsubscribe());
    this.removeDrawerBackHandler?.();
    this.removeDrawerBackHandler = null;
    clearAllBodyScrollLocks();
    this.activeScrollLockTarget = null;
  }

  private observeSidebarOffset(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const wrapper = this.layoutWrapperRef?.nativeElement;
    if (!wrapper) {
      return;
    }

    this.zone.runOutsideAngular(() => {
      let navigationBar: HTMLElement | null = null;
      let previousOffset: string | undefined;
      const scheduleUpdate = (): void => {
        if (this.destroyed || this.sidebarFrameId !== null) {
          return;
        }
        this.sidebarFrameId = window.requestAnimationFrame(() => {
          this.sidebarFrameId = null;
          if (this.destroyed) {
            return;
          }
          // Measure the rendered bar, not its Angular host (which may be inline).
          const bottom = Math.max(0, navigationBar?.getBoundingClientRect().bottom ?? 0);
          const offset = `max(var(--screen-safe-area-top, 0px), ${bottom}px)`;
          if (offset !== previousOffset) {
            wrapper.style.setProperty('--site-sidebar-top', offset);
            wrapper.setAttribute('data-sidebar-geometry', '');
            previousOffset = offset;
          }
        });
      };

      if (typeof ResizeObserver !== 'undefined') {
        this.sidebarResizeObserver = new ResizeObserver(scheduleUpdate);
      }
      const observeNavigationBar = (): void => this.zone.runOutsideAngular(() => {
        this.sidebarResizeObserver?.disconnect();
        navigationBar = this.navigationBarRefs.first?.nativeElement
          .querySelector<HTMLElement>('.layout-topbar, .mobile-app-bar') ?? null;
        if (navigationBar) {
          this.sidebarResizeObserver?.observe(navigationBar, {box: 'border-box'});
        }
        const safeArea = wrapper.querySelector<HTMLElement>('.safe-area');
        if (safeArea) {
          this.sidebarResizeObserver?.observe(safeArea, {box: 'border-box'});
        }
        scheduleUpdate();
      });

      // Query changes cover *ngIf removal and switching between the two bars.
      this.subscriptions.push(this.navigationBarRefs.changes.subscribe(observeNavigationBar));
      observeNavigationBar();
      window.addEventListener('scroll', scheduleUpdate, {passive: true});
      window.addEventListener('resize', scheduleUpdate, {passive: true});
      this.removeSidebarListeners = () => {
        window.removeEventListener('scroll', scheduleUpdate);
        window.removeEventListener('resize', scheduleUpdate);
      };
    });
  }

  private observeBreadcrumbHeight(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const wrapper = this.layoutWrapperRef?.nativeElement;
    const breadcrumb = this.breadcrumbRef?.nativeElement;
    if (!wrapper || !breadcrumb) {
      return;
    }

    const updateHeight = (): void => {
      wrapper.style.setProperty('--screen-breadcrumb-height', `${breadcrumb.getBoundingClientRect().height}px`);
    };
    updateHeight();

    if (typeof ResizeObserver !== 'undefined') {
      this.breadcrumbResizeObserver = new ResizeObserver(updateHeight);
      this.breadcrumbResizeObserver.observe(breadcrumb);
    }
  }

  private resolveScrollLockTarget(): HTMLElement | null {
    if (this.activeTopbarItem === 'notifications' || this.rightMenuActive || this.mobileSearchOverlayOpen) {
      return document.body;
    }

    if (this.mobileMenuActive) {
      return this.mobileMenuDrawerRef?.nativeElement ?? document.body;
    }

    return document.body;
  }

  private registerDrawerBackHandler(): void {
    this.removeDrawerBackHandler?.();
    this.removeDrawerBackHandler = this.mobileBack.register('site-layout-mobile-drawer', () => {
      if (!this.mobileMenuActive) {
        return false;
      }
      this.closeMobileDrawer();
      return true;
    });
  }
}
